import { useEffect, useImperativeHandle, useRef, useState } from 'react'

import {
  DSAlert,
  DSButton,
  DSCircularLoading,
  DSCol,
  DSDivider,
  DSModal,
  DSRow,
  DSText
} from '@vr/ds-react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'

import { BoxContent, BoxHeader, BoxRounded } from './styled'
import { FormValues } from './type'
import { Collect } from '../collect'
import { Fees } from '../fees'
import { ReleaseDate } from '../release-date'
import { FormFilterRef, ModalRequestLogisticsProps } from './interface'
import { TableCollaborator } from './table-collaborator'
import { CardIssuance } from '../card-issuance'
import { Observations } from '../observations'
import { OrderNumber } from '../order-number'
import { useGeneralContext } from '@/contexts/general.context'
import { useLogisticsPost } from '@/hooks/use-logistics-post'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'

const DEFAULT_VALUES: FormValues = {
  arquivo: null,
  colaboradores: [],
  dataLiberacao: null,
  numeroPedidoOperadora: null,
  observacoes: '',
  operadora: '',
  pedido: '',
  postoDeColeta: null,
  taxaDeRetirada: '',
  viaDosCartoes: null
}

const ModalRequestLogistics = ({
  show = true,
  onClose = () => {},
  operator,
  items,
  setRequestLogisticsSuccess,
  setRequestLogisticsError
}: ModalRequestLogisticsProps) => {
  const formRef = useRef<HTMLFormElement>(null)
  const imperativeRef = useRef<FormFilterRef>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [accountKey, setAccountKey] = useState(Date.now())
  const [fileUploaded, setFileUploaded] = useState<File[]>([])
  const [errorUploaded, setErrorUploaded] = useState<string | null>(null)

  const { handleSelectItem } = useGeneralContext()
  const { logisticsPost, isLoading } = useLogisticsPost()

  const userString = localStorage.getItem('user')
  const user = userString ? JSON.parse(userString) : {}
  const userId = user?.userId

  const methods = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange'
  })

  const cardIssuance = useWatch({
    name: 'viaDosCartoes',
    control: methods.control
  })

  useEffect(() => {
    setAccountKey(Date.now())
  }, [])

  useImperativeHandle(imperativeRef, () => ({
    submitForm: () => {
      formRef.current?.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      )
    }
  }))

  const handleButtonClick = () => {
    fileInputRef.current.click()
  }

  const onSubmit = async (data: FormValues) => {
    const dto = {
      arquivo: fileUploaded,
      atualizadoPor: String(userId),
      colaboradores: items.map((colab: { id: string }) => colab.id),
      dataLiberacao: formatBrDateToIso(data.dataLiberacao),
      idFornecedor: String(operator.idFornecedor),
      nomeOperadora: String(operator.nomeOperadora),
      numeroPedidoOperadora: String(data.numeroPedidoOperadora ?? ''),
      observacoes: data.observacoes || '',
      operadora: String(operator.idOperadora),
      pedido: String(operator.pedido),
      postoDeColeta: data.postoDeColeta ? String(data.postoDeColeta.value) : '',
      taxaDeRetirada: (
        Number(data.taxaDeRetirada.replace(/[^\d]+/g, '')) / 100
      ).toString(),
      viaDosCartoes: data.viaDosCartoes ? String(data.viaDosCartoes.value) : ''
    }
    const response = await logisticsPost(dto)

    if (response?.isSuccess) {
      onClose()
      setRequestLogisticsSuccess(true)
    } else {
      setRequestLogisticsError(true)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files

    if (!files) return

    Array.from(files).forEach(file => {
      const maxSize = 10 * 1024 * 1024 // 10MB
      const allowedExtensions = ['pdf']
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      if (!allowedExtensions.includes(fileExtension)) {
        setErrorUploaded('O arquivo selecionado deve ser do tipo .pdf')
        setFileUploaded([])
        return
      }
      if (file.size > maxSize) {
        setErrorUploaded(
          'O arquivo selecionado excede o tamanho máximo permitido de 10MB.'
        )
        setFileUploaded([])
        return
      }
      setFileUploaded(prevItems => [...prevItems, file])
      methods.setValue('arquivo', file, { shouldValidate: true })
    })
  }

  const handleRemove = (itemId: string[]) => {
    handleSelectItem(itemId, false)
  }

  return (
    <DSModal config={{ show, onClose }} css={{ width: 1000 }}>
      <FormProvider {...methods}>
        <form
          id='form-request-logistics'
          onSubmit={methods.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <DSModal.Header onClose={onClose}>Solicitar logística</DSModal.Header>
          {isLoading ? (
            <>
              <DSModal.Body>
                <DSCircularLoading isLoading shouldBlur={false} />
              </DSModal.Body>
              <DSModal.Footer></DSModal.Footer>
            </>
          ) : (
            <>
              <DSModal.Body>
                <BoxHeader>
                  <DSRow wrap='wrap'>
                    <DSCol
                      sm={12}
                      direction='column'
                      css={{ display: 'flex', gap: '$sm4' }}
                    >
                      <DSText>Operadora</DSText>
                      <DSText weight='bold'>
                        {operator.nomeOperadora || '-'}
                      </DSText>
                    </DSCol>
                  </DSRow>
                   <DSDivider css={{ margin: '24px 0' }} />
                  <DSRow wrap='wrap'>
                    <DSCol
                      sm={12}
                      direction='column'
                      css={{ display: 'flex', gap: '$sm4' }}
                    >
                      <DSText>
                        Fornecedor
                      </DSText>
                      <DSText weight='bold'>
                        {operator.nomeFornecedor || '-'}
                      </DSText>
                      {!operator.nomeFornecedor && (
                        <DSText
                          as='span'
                          size='sm'
                          weight='bold'
                          color='errorLight'
                        >
                          Fornecedor é obrigatório
                        </DSText>
                      )}
                    </DSCol>
                  </DSRow>
                </BoxHeader>
              </DSModal.Body>
              <DSModal.Body>
                <BoxContent>
                  <DSRow wrap='wrap'>
                    <DSCol xs={12} lg={6} gap={2}>
                      <ReleaseDate
                        methods={methods}
                        accountKey={accountKey}
                        show={show}
                      />
                    </DSCol>
                    <DSCol xs={12} lg={6} gap={2}>
                      <Fees methods={methods} accountKey={accountKey} />
                    </DSCol>
                    <DSCol xs={12} lg={6} gap={2}>
                      <OrderNumber methods={methods} accountKey={accountKey} />
                    </DSCol>
                    <DSCol xs={12} lg={6} gap={2}>
                      <CardIssuance methods={methods} accountKey={accountKey} />
                    </DSCol>
                    <DSCol xs={12} lg={6} gap={2}>
                      <Collect
                        methods={methods}
                        accountKey={String(cardIssuance)}
                        operator={operator}
                      />
                    </DSCol>
                  </DSRow>
                 
                </BoxContent>

                <Observations methods={methods} accountKey={accountKey} />

                <DSText
                  as='p'
                  size='sm'
                  weight='semibold'
                  style={{ margin: '8px 0' }}
                >
                  Anexar arquivo de liberação*
                </DSText>
                <Controller
                  key={accountKey}
                  name='arquivo'
                  control={methods.control}
                  rules={{
                    required: 'Arquivo de liberação é obrigatório'
                  }}
                  render={() => (
                    <BoxRounded
                      css={{
                        border: '1px solid $neutralVeryDark',
                        padding: '$sm16',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '$sm16',
                        marginBottom: '$sm24'
                      }}
                    >
                      <DSButton variant='secondary' onClick={handleButtonClick}>
                        Escolher arquivo
                      </DSButton>
                      <input
                        type='file'
                        onChange={handleFileChange}
                        accept='.pdf'
                        multiple={false}
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        data-testid='file-input'
                      />
                      <div className='self-center'>
                        {fileUploaded.length > 0 && (
                          <DSAlert
                            type='success'
                            hiddenBtnClose
                            css={{ padding: '$sm8 $sm20' }}
                          >
                            {fileUploaded.map((file, index) => (
                              <div key={index}>
                                {file.name} - {Math.round(file.size / 1024)} KB
                              </div>
                            ))}
                          </DSAlert>
                        )}

                        {errorUploaded && (
                          <DSAlert
                            type='error'
                            hiddenBtnClose
                            css={{ padding: '$sm8 $sm20' }}
                          >
                            {errorUploaded}
                          </DSAlert>
                        )}
                      </div>
                    </BoxRounded>
                  )}
                />
                {typeof methods.formState.errors.arquivo?.message ===
                  'string' && (
                  <DSText
                    as='span'
                    size='sm'
                    weight='semibold'
                    color='errorLight'
                  >
                    {methods.formState.errors.arquivo.message}
                  </DSText>
                )}

                <TableCollaborator items={items} handleRemove={handleRemove} />
              </DSModal.Body>
              <DSModal.Footer>
                <DSRow wrap='wrap' align='center'>
                  <DSCol
                    xs={12}
                    lg={12}
                    gap={4}
                    direction='row'
                    style={{ display: 'flex' }}
                    justify='end'
                  >
                    <DSButton variant='ghost' outlined onClick={onClose}>
                      Cancelar
                    </DSButton>
                    <DSButton variant='primary' type='submit'>
                      Confirmar
                    </DSButton>
                  </DSCol>
                </DSRow>
              </DSModal.Footer>
            </>
          )}
        </form>
      </FormProvider>
    </DSModal>
  )
}

export default ModalRequestLogistics
